/**
 * Utilidades para el formateo de fechas con zona horaria de Perú (America/Lima, UTC-5)
 */

const PERU_TIMEZONE = 'America/Lima';
const PERU_LOCALE = 'es-PE';

/**
 * Convierte un valor de fecha a Date, manejando correctamente strings YYYY-MM-DD
 * que pueden causar problemas de zona horaria al ser interpretados como UTC.
 * Usar esta función siempre que se reciba una fecha en formato string YYYY-MM-DD.
 */
export const toSafeDate = (date: Date | string | number): Date => {
  if (date instanceof Date) return date;
  if (typeof date === 'number') return new Date(date);

  // Si es string en formato YYYY-MM-DD, agregar T12:00:00 para evitar problemas de zona horaria
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return new Date(date + 'T12:00:00');
  }

  return new Date(date);
};

/**
 * Formatea una fecha para mostrar en formato local peruano
 * @param date - Fecha a formatear (Date, string, o timestamp)
 * @param options - Opciones de formato adicionales
 * @returns Fecha formateada en español peruano
 */
export const formatDatePeru = (
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const dateObj = toSafeDate(date);

  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: PERU_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options
  };

  return dateObj.toLocaleDateString(PERU_LOCALE, defaultOptions);
};

/**
 * Formatea una fecha para mostrar fecha y hora en formato peruano
 * @param date - Fecha a formatear
 * @param options - Opciones de formato adicionales
 * @returns Fecha y hora formateada
 */
export const formatDateTimePeru = (
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const dateObj = toSafeDate(date);

  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: PERU_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...options
  };

  return dateObj.toLocaleDateString(PERU_LOCALE, defaultOptions);
};

/**
 * Formatea solo la hora en formato peruano
 * @param date - Fecha a formatear
 * @param options - Opciones de formato adicionales
 * @returns Hora formateada
 */
export const formatTimePeru = (
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const dateObj = toSafeDate(date);

  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: PERU_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...options
  };

  return dateObj.toLocaleTimeString(PERU_LOCALE, defaultOptions);
};

/**
 * Formatea una fecha para mostrar el día completo (ej: "miércoles, 28 de agosto de 2024")
 * @param date - Fecha a formatear
 * @returns Fecha completa formateada
 */
export const formatFullDatePeru = (date: Date | string | number = new Date()): string => {
  const dateObj = toSafeDate(date);

  return dateObj.toLocaleDateString(PERU_LOCALE, {
    timeZone: PERU_TIMEZONE,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Obtiene la fecha actual en zona horaria de Perú como string YYYY-MM-DD
 * @returns String con la fecha en formato YYYY-MM-DD
 */
export const getCurrentDatePeru = (): string => {
  return new Date().toLocaleDateString('en-CA', { timeZone: PERU_TIMEZONE });
};

/**
 * Formatea una fecha para usar en inputs de tipo date
 * @param date - Fecha a formatear
 * @returns Fecha en formato YYYY-MM-DD
 */
export const formatDateForInput = (date: Date | string | number): string => {
  const dateObj = toSafeDate(date);
  // Usar Intl.DateTimeFormat con locale 'en-CA' que da formato YYYY-MM-DD directamente
  return dateObj.toLocaleDateString('en-CA', { timeZone: PERU_TIMEZONE });
};

/**
 * Calcula la diferencia en días entre dos fechas en zona horaria de Perú
 * @param date1 - Primera fecha
 * @param date2 - Segunda fecha
 * @returns Diferencia en días
 */
export const getDaysDifferenceInPeru = (
  date1: Date | string | number,
  date2: Date | string | number
): number => {
  // Convertir ambas fechas a strings YYYY-MM-DD en Peru timezone y luego calcular diferencia
  const d1Str = new Date(date1).toLocaleDateString('en-CA', { timeZone: PERU_TIMEZONE });
  const d2Str = new Date(date2).toLocaleDateString('en-CA', { timeZone: PERU_TIMEZONE });

  const d1 = new Date(d1Str + 'T00:00:00');
  const d2 = new Date(d2Str + 'T00:00:00');

  const timeDiff = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(timeDiff / (1000 * 3600 * 24));
};